import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { api } from '../utils/api';

const EditLicenseDialog = ({ license, open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    license_number: '',
    npn: '',
    issue_date: '',
    effective_date: '',
    expiration_date: '',
    owner: '',
    status: 'active'
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (license) {
      setFormData({
        license_number: license.license_number || '',
        npn: license.npn || '',
        issue_date: license.issue_date ? new Date(license.issue_date).toISOString().split('T')[0] : '',
        effective_date: license.effective_date ? new Date(license.effective_date).toISOString().split('T')[0] : '',
        expiration_date: license.expiration_date ? new Date(license.expiration_date).toISOString().split('T')[0] : '',
        owner: license.owner || '',
        status: license.status || 'active'
      });
    }
  }, [license]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updates = {
        license_number: formData.license_number,
        npn: formData.npn,
        owner: formData.owner,
        status: formData.status
      };
      
      if (formData.issue_date) {
        updates.issue_date = new Date(formData.issue_date).toISOString();
      }
      if (formData.effective_date) {
        updates.effective_date = new Date(formData.effective_date).toISOString();
      }
      if (formData.expiration_date) {
        updates.expiration_date = new Date(formData.expiration_date).toISOString();
      }

      await api.updateLicense(license.license_id, updates);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to update license:', error);
      alert('Failed to update license');
    } finally {
      setLoading(false);
    }
  };

  if (!license) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="edit-license-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit License</DialogTitle>
          <DialogDescription>
            Update license information for {license.license_type} - {license.state}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label htmlFor="license_number">License Number</Label>
              <Input
                id="license_number"
                value={formData.license_number}
                onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                placeholder="Enter license number"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="npn">NPN</Label>
                <Input
                  id="npn"
                  value={formData.npn}
                  onChange={(e) => setFormData({ ...formData, npn: e.target.value })}
                  placeholder="National Producer Number"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="owner">Owner Email</Label>
                <Input
                  id="owner"
                  type="email"
                  value={formData.owner}
                  onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                  placeholder="owner@losspay.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="issue_date">Issue Date</Label>
                <Input
                  id="issue_date"
                  type="date"
                  value={formData.issue_date}
                  onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="effective_date">Effective Date</Label>
                <Input
                  id="effective_date"
                  type="date"
                  value={formData.effective_date}
                  onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expiration_date">Expiration Date</Label>
              <Input
                id="expiration_date"
                type="date"
                value={formData.expiration_date}
                onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending_renewal">Pending Renewal</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="withdrawn">Withdrawn</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditLicenseDialog;